import { useState, useCallback, useEffect } from 'react';
import { ChatAgentType } from '@constants/chat-agents';
import type { ChatMessage } from '../types';
import { SenderType } from '../types/chat-message';
import { saveChatHistories, saveCursors } from '@utils/chat-storage';
import { alignBlockContentsWithFullContent, isSimilarId } from '@utils/chat-message';

// Interface for message with source tracking
interface ChatMessageWithSource extends ChatMessage {
  _isHistory?: boolean;
  _sequence?: number;
}

interface UseChatHistoryReturn {
  chatHistories: Record<ChatAgentType, ChatMessage[]>;
  hasChatHistories: Record<ChatAgentType, boolean | null>;
  cursors: Record<ChatAgentType, string | null>;
  previousCursors: Record<ChatAgentType, string | null>;
  addMessage: (agentType: ChatAgentType, message: ChatMessage) => void;
  updateMessageStream: (agentType: ChatAgentType, messageId: string | number, updates: Partial<ChatMessage>) => void;
  prependMessages: (agentType: ChatAgentType, messages: ChatMessage[], nextCursor: string | null) => void;
  appendMessages: (agentType: ChatAgentType, messages: ChatMessage[], previousCursor: string | null) => void;
  replaceChatLog: (agentType: ChatAgentType, messages: ChatMessage[], nextCursor: string | null, previousCursor: string | null) => void;
  setHasChatHistory: (agentType: ChatAgentType, hasHistory: boolean | null, nextCursor?: string | null) => void;
  getCurrentChatLog: (agentType: ChatAgentType) => ChatMessage[];
  updateMessage: (agentType: ChatAgentType, messageId: string, updates: Partial<ChatMessage>) => void;
  setAllHistories: (
    histories: Record<ChatAgentType, ChatMessage[]>,
    newCursors: Record<ChatAgentType, string | null>,
    newHasChatHistories: Record<ChatAgentType, boolean>
  ) => void;
  resetAllHistories: () => void;
  markAllAsHistory: () => void;
  clearStoppedMessages: () => void;
}

const initialChatHistories: Record<ChatAgentType, ChatMessage[]> = {
  [ChatAgentType.AI]: [],
  [ChatAgentType.CSKH]: [],
  [ChatAgentType.TAX_QUESTION]: [],
  [ChatAgentType.INSTRUCTION_MANUAL]: [],
  [ChatAgentType.ANALYTICS_QUESTION]: [],
};

const initialHasChatHistories: Record<ChatAgentType, boolean | null> = {
  [ChatAgentType.AI]: null,
  [ChatAgentType.CSKH]: null,
  [ChatAgentType.TAX_QUESTION]: null,
  [ChatAgentType.INSTRUCTION_MANUAL]: null,
  [ChatAgentType.ANALYTICS_QUESTION]: null,
};

const initialCursors: Record<ChatAgentType, string | null> = {
  [ChatAgentType.AI]: null,
  [ChatAgentType.CSKH]: null,
  [ChatAgentType.TAX_QUESTION]: null,
  [ChatAgentType.INSTRUCTION_MANUAL]: null,
  [ChatAgentType.ANALYTICS_QUESTION]: null,
};

const cleanFinalMessageText = (text: string): string => {
  if (!text) return '';
  let processed = text;

  if (processed.includes('|')) {
    processed = processed.split('|')[0];
  }

  return processed.trim();
};

// Sequence counter for new messages to maintain insertion order
let globalSequenceCounter = 0;

export const useChatHistory = (): UseChatHistoryReturn => {
  // Khởi tạo với giá trị rỗng, API sẽ load data
  const [chatHistories, setChatHistories] = useState<Record<ChatAgentType, ChatMessage[]>>(
    initialChatHistories
  );
  const [hasChatHistories, setHasChatHistories] = useState<Record<ChatAgentType, boolean | null>>(
    initialHasChatHistories
  );
  const [cursors, setCursors] = useState<Record<ChatAgentType, string | null>>(
    initialCursors
  );
  const [previousCursors, setPreviousCursors] = useState<Record<ChatAgentType, string | null>>(
    initialCursors
  );

  // Auto-save vào localStorage khi chatHistories thay đổi
  useEffect(() => {
    saveChatHistories(chatHistories);
  }, [chatHistories]);

  // Auto-save cursors khi thay đổi
  useEffect(() => {
    saveCursors(cursors);
  }, [cursors]);

  const addMessage = useCallback((agentType: ChatAgentType, message: ChatMessage) => {
    setChatHistories((prev) => {
      const currentMessages = prev[agentType] || [];

      // Check for duplicate before adding
      let duplicateMsg: any = null;
      let duplicateReason = '';
      const isDuplicate = currentMessages.some((msg) => {
        // 1. LUÔN check theo ID (đáng tin cậy nhất cho cả BOT và USER)
        if (msg.messageId && message.messageId && isSimilarId(msg.messageId, message.messageId)) {
          duplicateMsg = msg;
          duplicateReason = `msg.messageId === message.messageId (${msg.messageId} === ${message.messageId})`;
          return true;
        }

        // 2. Check các trường ID đặc biệt khác của hệ thống
        if (msg.senderType === SenderType.BOT && message.senderType === SenderType.BOT &&
          msg.msgBotFwToNewAgentId && message.msgBotFwToNewAgentId &&
          msg.msgBotFwToNewAgentId === message.msgBotFwToNewAgentId) {
          duplicateMsg = msg;
          duplicateReason = `msg.msgBotFwToNewAgentId === message.msgBotFwToNewAgentId (${msg.msgBotFwToNewAgentId} === ${message.msgBotFwToNewAgentId})`;
          return true;
        }

        // Check if message has msgBotFwToNewAgentId matching existing message's messageId
        if (msg.senderType === SenderType.BOT && message.senderType === SenderType.BOT &&
          message.msgBotFwToNewAgentId && msg.messageId &&
          message.msgBotFwToNewAgentId === msg.messageId) {
          duplicateMsg = msg;
          duplicateReason = `message.msgBotFwToNewAgentId === msg.messageId (${message.msgBotFwToNewAgentId} === ${msg.messageId})`;
          return true;
        }

        // Check if existing message has msgBotFwToNewAgentId matching new message's messageId
        if (msg.senderType === SenderType.BOT && message.senderType === SenderType.BOT &&
          msg.msgBotFwToNewAgentId && message.messageId &&
          msg.msgBotFwToNewAgentId === message.messageId) {
          duplicateMsg = msg;
          duplicateReason = `msg.msgBotFwToNewAgentId === message.messageId (${msg.msgBotFwToNewAgentId} === ${message.messageId})`;
          return true;
        }

        // Check by msgUserFwCodeMessage (for forwarded user messages)
        if (msg.senderType === SenderType.USER && message.senderType === SenderType.USER &&
          msg.msgUserFwCodeMessage && message.msgUserFwCodeMessage &&
          msg.msgUserFwCodeMessage === message.msgUserFwCodeMessage) {
          duplicateMsg = msg;
          duplicateReason = `msg.msgUserFwCodeMessage === message.msgUserFwCodeMessage (${msg.msgUserFwCodeMessage} === ${message.msgUserFwCodeMessage})`;
          return true;
        }

        // // Check by codeMessage (UUID matching between frontend optimistic and backend database)
        // if (msg.codeMessage && message.codeMessage &&
        //   msg.codeMessage === message.codeMessage) {
        //   duplicateMsg = msg;
        //   duplicateReason = `msg.codeMessage === message.codeMessage (${msg.codeMessage} === ${message.codeMessage})`;
        //   return true;
        // }

        // TEMP disabled: allow duplicate USER text (e.g. same quick action clicked twice).
        // Re-enable if we need to suppress optimistic + socket echo of the same send.
        // // 3. CHỈ lọc theo text + time đối với tin nhắn USER
        // // Bot message tuyệt đối không lọc theo text vì Bot có thể gửi nhiều tin giống nhau (error, retry, v.v.)
        // if (message.senderType === SenderType.USER &&
        //   msg.senderType === SenderType.USER &&
        //   msg.text === message.text) {
        //   const timeA = new Date(msg.createdAt || msg.time || 0).getTime();
        //   const timeB = new Date(message.createdAt || message.time || 0).getTime();
        //   const timeDiff = Math.abs(timeA - timeB);
        //
        //   // Nếu trùng text và sender là user, và trong vòng 5 giây, coi là trùng (do UI add trước)
        //   if (timeDiff < 5000) {
        //     duplicateMsg = msg;
        //     duplicateReason = `User text + time similarity (${timeDiff}ms)`;
        //     return true;
        //   }
        // }

        return false;
      });

      // console.log(`[addMessage] Agent: ${agentType}, text: "${message.text?.substring(0, 30)}...", sender: ${message.senderType}, isDuplicate: ${isDuplicate}, reason: ${duplicateReason}`, isDuplicate ? { duplicateMsg, incomingMsg: message } : '');

      // Only add if not duplicate
      if (isDuplicate) {
        return prev; // Return unchanged state
      }

      // Preserve _isHistory if it comes from server, otherwise default to false
      const isHistory = (message as ChatMessageWithSource)._isHistory ?? false;
      
      const newMessage: ChatMessageWithSource = {
        ...message,
        _isHistory: isHistory,
        _sequence: ++globalSequenceCounter,
      };

      return {
        ...prev,
        [agentType]: [...currentMessages, newMessage],
      };
    });
  }, []);

  const updateMessageStream = useCallback((
    agentType: ChatAgentType,
    messageId: string | number,
    incomingUpdates: Partial<ChatMessage>
  ) => {
    const searchId = messageId !== undefined && messageId !== null ? String(messageId) : '';
    if (!searchId) return;

    setChatHistories((prev) => {
      const currentMessages = prev[agentType] || [];

      const messageIndex = currentMessages.findIndex(
        (msg) =>
          !msg._isHistory && (
            (msg.messageId !== undefined && isSimilarId(msg.messageId, searchId)) ||
            (msg.id !== undefined && isSimilarId(msg.id, searchId)) ||
            (msg.codeMessage !== undefined && String(msg.codeMessage) === searchId && msg.senderType === SenderType.USER)
          )
      );

      let finalIndex = messageIndex;
      if (finalIndex === -1 && currentMessages.length > 0) {
        const lastMsg = currentMessages[currentMessages.length - 1];
        if (lastMsg.senderType === SenderType.BOT && !lastMsg._isHistory) {
          finalIndex = currentMessages.length - 1;
        }
      }

      if (finalIndex === -1) return prev;

      const currentMsg = currentMessages[finalIndex];
      const oldContent = currentMsg.text || currentMsg.fullContent || '';
      const incomingContent = incomingUpdates.text || incomingUpdates.fullContent || '';

      const isFinalChunk = (incomingUpdates as any).isFinal === true;
      const safeMetaUpdates = { ...incomingUpdates };

      // Gộp blockContents thay vì ghi đè nếu blockContents mới chỉ chứa các block TEXT rỗng (chứa metadata gợi ý/tham chiếu)
      if (currentMsg.blockContents && currentMsg.blockContents.length > 0 && safeMetaUpdates.blockContents) {
        const hasNewText = safeMetaUpdates.blockContents.some((b: any) => (b.type === 'TEXT' && b.payload?.text) || (b.type !== 'TEXT'));
        if (!hasNewText) {
          const newReferences = safeMetaUpdates.references || [];
          const newFollowUpQuestions = safeMetaUpdates.followUpQuestions || [];
          const newButtons = safeMetaUpdates.buttons || [];

          safeMetaUpdates.blockContents = currentMsg.blockContents.map((block: any) => {
            if (block.type === 'TEXT') {
              const mergedReferences = [...(block.payload?.references || []), ...newReferences];
              const mergedFollowUps = [...(block.payload?.followUpQuestions || []), ...newFollowUpQuestions];
              const mergedButtons = [...(block.payload?.buttons || []), ...newButtons];

              // Deduplicate merged references
              const uniqueRefs: any[] = [];
              const refKeys = new Set<string>();
              mergedReferences.forEach((ref: any) => {
                const key = ref?.uri || ref?.title || '';
                if (key && !refKeys.has(key)) {
                  refKeys.add(key);
                  uniqueRefs.push(ref);
                } else if (!key) {
                  uniqueRefs.push(ref);
                }
              });

              // Deduplicate merged follow-up questions
              const uniqueFollows = [...new Set(mergedFollowUps)];

              // Deduplicate merged buttons
              const uniqueBtns: any[] = [];
              const btnKeys = new Set<string>();
              mergedButtons.forEach((btn: any) => {
                const key = btn.id !== undefined && btn.id !== null ? String(btn.id) : btn.name;
                if (!btnKeys.has(key)) {
                  btnKeys.add(key);
                  uniqueBtns.push(btn);
                }
              });

              return {
                ...block,
                payload: {
                  ...block.payload,
                  references: uniqueRefs,
                  followUpQuestions: uniqueFollows,
                  buttons: uniqueBtns,
                }
              };
            }
            return block;
          });
        }
      }

      // TRƯỜNG HỢP A: KHI NHẬN GÓI TIN CHỐT KẾT THÚC STREAM (isFinal = true)
      if (isFinalChunk) {
        delete safeMetaUpdates.text;
        delete safeMetaUpdates.fullContent;

        if (safeMetaUpdates.blockContents && safeMetaUpdates.blockContents.length === 0) {
          delete safeMetaUpdates.blockContents;
        }

        // Kích hoạt bộ dọn dẹp để cấu trúc lại toàn bộ Markdown lỗi từ core sinh ra
        const cleanedText = cleanFinalMessageText(oldContent);

        // Align finalized blockContents with the accumulated full text
        const alignedBlocks = alignBlockContentsWithFullContent(
          safeMetaUpdates.blockContents || currentMsg.blockContents,
          cleanedText
        );

        const updatedMessages = [...currentMessages];
        updatedMessages[finalIndex] = {
          ...currentMsg,
          ...safeMetaUpdates,
          text: cleanedText,
          fullContent: cleanedText,
          blockContents: alignedBlocks || [],
          isQueuing: false,
          isThinking: false,
          _isHistory: false,
          isFinal: true, // Đồng bộ biến cờ báo kết thúc để UI biết đường render linh hoạt
        } as any;

        return { ...prev, [agentType]: updatedMessages };
      }

      // TRƯỜNG HỢP B: ĐANG NHẬN CÁC CHUNKS CHỮ STREAM BÌNH THƯỜNG
      console.log('Updating message stream. Agent:', incomingContent);
      let mergedContent = oldContent + incomingContent;

      // Khi đang chạy chữ, chủ động ẩn ngay dấu hỏi gợi ý '|' tránh giật UI đột ngột
      if (mergedContent.includes('|')) {
        mergedContent = mergedContent.split('|')[0];
      }

      if (
        !incomingContent ||
        incomingContent.trim() === '' ||
        (safeMetaUpdates.blockContents && safeMetaUpdates.blockContents.length === 0)
      ) {
        delete safeMetaUpdates.blockContents;
        delete safeMetaUpdates.fullContent;
      }
      const updatedMessages = [...currentMessages];
      updatedMessages[finalIndex] = {
        ...currentMsg,
        ...safeMetaUpdates,
        text: mergedContent,
        fullContent: mergedContent,
        isQueuing: false,
        isThinking: false,
        _isHistory: false,
        isFinal: false
      } as any;

      return { ...prev, [agentType]: updatedMessages };
    });
  }, []);

  const prependMessages = useCallback((agentType: ChatAgentType, messages: ChatMessage[], nextCursor: string | null) => {
    // Mark all messages as history (sorted by time)
    const historyMessages: ChatMessageWithSource[] = messages.map(msg => ({
      ...msg,
      _isHistory: true,
      isFinal: true,
    }));

    setChatHistories((prev) => {
      const currentMessages = prev[agentType] || [];

      // Dedup: filter out messages that already exist in state
      const existingIds = new Set(
        currentMessages.map(m => m.messageId).concat(currentMessages.map(m => m.id))
      );
      const newMessages = historyMessages.filter(
        msg => !existingIds.has(msg.messageId) && !existingIds.has(msg.id)
      );

      if (newMessages.length === 0) {
        return prev;
      }

      return {
        ...prev,
        [agentType]: [...newMessages, ...currentMessages],
      };
    });
    setCursors((prev) => ({
      ...prev,
      [agentType]: nextCursor,
    }));
  }, []);

  // Append messages to the end (for loading newer messages in CSKH)
  const appendMessages = useCallback((agentType: ChatAgentType, messages: ChatMessage[], previousCursor: string | null) => {
    const historyMessages: ChatMessageWithSource[] = messages.map(msg => ({
      ...msg,
      _isHistory: true,
      isFinal: true,
    }));

    setChatHistories((prev) => {
      const currentMessages = prev[agentType] || [];
      const existingIds = new Set(
        currentMessages.map(m => m.messageId).concat(currentMessages.map(m => m.id))
      );
      const newMessages = historyMessages.filter(
        msg => !existingIds.has(msg.messageId) && !existingIds.has(msg.id)
      );

      if (newMessages.length === 0) {
        return prev;
      }

      return {
        ...prev,
        [agentType]: [...currentMessages, ...newMessages],
      };
    });
    setPreviousCursors((prev) => ({
      ...prev,
      [agentType]: previousCursor,
    }));
  }, []);

  // Replace entire chat log for an agent (for page-index jump in CSKH)
  const replaceChatLog = useCallback((
    agentType: ChatAgentType,
    messages: ChatMessage[],
    nextCursor: string | null,
    previousCursor: string | null
  ) => {
    const historyMessages: ChatMessageWithSource[] = messages.map(msg => ({
      ...msg,
      _isHistory: true,
      isFinal: true,
    }));

    setChatHistories((prev) => ({
      ...prev,
      [agentType]: historyMessages,
    }));
    setCursors((prev) => ({
      ...prev,
      [agentType]: nextCursor,
    }));
    setPreviousCursors((prev) => ({
      ...prev,
      [agentType]: previousCursor,
    }));
  }, []);

  const setHasChatHistory = useCallback((agentType: ChatAgentType, hasHistory: boolean | null, nextCursor: string | null = null) => {
    setHasChatHistories((prev) => ({
      ...prev,
      [agentType]: hasHistory,
    }));
    if (nextCursor !== undefined) {
      setCursors((prev) => {
        // Don't overwrite existing cursor with null (prevents race condition)
        if (nextCursor === null && prev[agentType]) {
          return prev;
        }
        return { ...prev, [agentType]: nextCursor };
      });
    }
  }, []);

  const getCurrentChatLog = useCallback(
    (agentType: ChatAgentType) => {
      const messages = chatHistories[agentType] || [];

      // Separate history messages from new messages
      const historyMessages: ChatMessageWithSource[] = [];
      const newMessages: ChatMessageWithSource[] = [];

      for (const msg of messages) {
        const msgWithSource = msg as ChatMessageWithSource;
        if (msgWithSource._isHistory) {
          historyMessages.push(msgWithSource);
        } else {
          newMessages.push(msgWithSource);
        }
      }

      // // Sort history messages by time (oldest first)
      // historyMessages.sort((a, b) => {
      //   const timeA = new Date(a.createdAt || a.time || 0).getTime();
      //   const timeB = new Date(b.createdAt || b.time || 0).getTime();
      //   return timeA - timeB;
      // });

      // New messages maintain their insertion order (by _sequence)
      newMessages.sort((a, b) => {
        const seqA = a._sequence || 0;
        const seqB = b._sequence || 0;
        return seqA - seqB;
      });

      // Return combined: history (sorted by time) + new messages (sorted by sequence)
      return [...historyMessages, ...newMessages];
    },
    [chatHistories]
  );

  const updateMessage = useCallback((
    agentType: ChatAgentType,
    messageId: string | number,
    updates: Partial<ChatMessage> | Record<string, any>
  ) => {
    setChatHistories((prev) => {
      const currentMessages = prev[agentType] || [];
      const searchId = messageId !== undefined && messageId !== null ? String(messageId) : '';
      const messageIndex = currentMessages.findIndex(
        (msg) => 
          (msg.messageId !== undefined && isSimilarId(msg.messageId, searchId)) || 
          (msg.id !== undefined && isSimilarId(msg.id, searchId)) || 
          (msg.codeMessage !== undefined && String(msg.codeMessage) === searchId && msg.senderType === SenderType.USER)
      );

      if (messageIndex === -1) {
        console.warn('[ChatHistory] Message not found for update:', messageId);
        return prev;
      }

      const currentMsg = currentMessages[messageIndex];

      // Resolve updates: if a field value is a function, call it with the current value
      // This allows per-item updates like: imageUploadItems: (prev) => [...prev modified]
      const resolvedUpdates: Record<string, any> = {};
      for (const [key, value] of Object.entries(updates)) {
        if (typeof value === 'function') {
          resolvedUpdates[key] = value((currentMsg as any)[key]);
        } else {
          resolvedUpdates[key] = value;
        }
      }

      const updatedMessages = [...currentMessages];
      updatedMessages[messageIndex] = {
        ...currentMsg,
        ...resolvedUpdates,
      };

      return {
        ...prev,
        [agentType]: updatedMessages,
      };
    });
  }, []);

  // Set tất cả histories cùng lúc (dùng khi load initial)
  const setAllHistories = useCallback((
    histories: Record<ChatAgentType, ChatMessage[]>,
    newCursors: Record<ChatAgentType, string | null>,
    newHasChatHistories: Record<ChatAgentType, boolean>
  ) => {
    // Mark all loaded messages as history
    const markedHistories: Record<ChatAgentType, ChatMessage[]> = { ...initialChatHistories };

    Object.entries(histories).forEach(([agentType, messages]) => {
      markedHistories[agentType as ChatAgentType] = messages.map(msg => ({
        ...msg,
        _isHistory: true,
        isFinal: true,
      }));
    });

    setChatHistories(markedHistories);
    setCursors(newCursors);
    setHasChatHistories(newHasChatHistories as Record<ChatAgentType, boolean | null>);
  }, []);

  // Mark all messages as history (e.g. when closing chat)
  const markAllAsHistory = useCallback(() => {
    setChatHistories((prev) => {
      const newHistories: Record<ChatAgentType, ChatMessage[]> = { ...prev };

      Object.keys(newHistories).forEach((key) => {
        const agentType = key as ChatAgentType;
        newHistories[agentType] = newHistories[agentType].map(msg => ({
          ...msg,
          _isHistory: true,
          ...(msg.isFinal === false && { isFinal: true }),
        }));
      });

      return newHistories;
    });
  }, []);

  const clearStoppedMessages = useCallback(() => {
    setChatHistories((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((key) => {
        const agentType = key as ChatAgentType;
        next[agentType] = (next[agentType] || []).map((msg) => 
          msg.isStopped ? { ...msg, isStopped: false } : msg
        );
      });
      return next;
    });
  }, []);

  const resetAllHistories = useCallback(() => {
    globalSequenceCounter = 0;
    setChatHistories(initialChatHistories);
    setHasChatHistories(initialHasChatHistories);
    setCursors(initialCursors);
    setPreviousCursors(initialCursors);
  }, []);

  return {
    chatHistories,
    hasChatHistories,
    cursors,
    previousCursors,
    updateMessageStream,
    addMessage,
    prependMessages,
    appendMessages,
    replaceChatLog,
    setHasChatHistory,
    getCurrentChatLog,
    updateMessage,
    setAllHistories,
    resetAllHistories,
    markAllAsHistory,
    clearStoppedMessages,
  };
};

